import { css } from '@emotion/react'
import { graphql } from 'gatsby'
import { type ComponentProps } from 'react'

import { DatoStructuredText } from '@/features/common/'
import { GatsbyImageFocused } from '@/features/common/'
import { MarkdownHeading } from '@/features/common/'
import { absoluteFill, mq } from '@/theme/mixins'
import { colors } from '@/theme/variables'

interface Props extends ComponentProps<'section'> {
  data: Queries.LandingHeroFragment | null | undefined
}

export const LandingHero = ({ data, ...props }: Props) => {
  const getImage = (index: number) => {
    const image = data?.images?.[index]
    switch (index) {
      case 0: {
        return {
          image: image?.image1,
          ar: 1,
        }
      }
      case 1: {
        return {
          image: image?.image2,
          ar: 4 / 5,
        }
      }
      case 2: {
        return {
          image: image?.image3,
          ar: 2 / 3,
        }
      }
      case 3: {
        return {
          image: image?.image4,
          ar: 9 / 5,
        }
      }
    }
  }

  const styles = {
    section: css`
      display: grid;
      grid-template-columns: 2fr 3fr;
      grid-template-rows: 1fr var(--row-144);
      column-gap: var(--gtr-36);
      min-height: min(
        (100svh - var(--nav-height) + var(--row-72)),
        100vw
      );
      background: ${colors.gray95};
      ${mq().m} {
        grid-template-columns: 1fr;
        grid-template-rows: auto 1fr var(--row-144);
      }
    `,
    background: css`
      position: relative;
      width: 100%;
      height: 100%;
      grid-column: 1 / -1;
      grid-row: 1 / 2;
      background-color: #000;
      [data-gatsby-image-wrapper] {
        ${absoluteFill}
      }
      ${mq().m} {
        grid-row: 1 / 3;
      }
    `,
    text: css`
      position: relative;
      grid-column: 1 / span 1;
      grid-row: 1 / 2;
      margin-left: var(--margin);
      color: #fff;
      ${mq().m} {
        margin-right: var(--margin);
      }
    `,
    heading: css`
      line-height: 1.25;
      font-size: var(--fs-72);
      margin: var(--row-108) 0;
      b {
        font-weight: inherit;
        color: ${colors.gray75};
      }
      em {
        font-family: var(--ff-body);
        color: ${colors.redLight};
        font-style: normal;
        font-weight: 700;
        display: inline-block;
      }
      ${mq().m} {
        margin-bottom: 0em;
      }
    `,
    caveat: css``,
    imageContainer: css`
      grid-column: span 1 / -1;
      grid-row: 1 / 3;
      margin: var(--row-54) var(--gtr-54) 0 0;
      display: grid;
      grid-template-columns: 5fr 33fr 50fr 10fr;
      grid-template-rows: 10fr 33fr 25fr 25fr 5fr;
      gap: 1rem;
      ${mq().m} {
        grid-row: 2 / 4;
        margin: var(--row-36) var(--margin) var(--row-54);
        height: 80vw;
        gap: 0.75rem;
      }
      ${mq().s} {
        gap: 0.5rem;
      }
    `,
    image: (i: number) => css`
      width: 100%;
      height: 100%;
      position: relative;
      [data-gatsby-image-wrapper] {
        ${absoluteFill}
      }
      ${i === 0 &&
      css`
        grid-column: 2 / 3;
        grid-row: 2 / 3;
      `}
      ${i === 1 &&
      css`
        grid-column: 3 / 5;
        grid-row: 1 / 4;
      `}
      ${i === 2 &&
      css`
        grid-column: 1 / 3;
        grid-row: 3 / 6;
      `}
      ${i === 3 &&
      css`
        grid-column: 3 / 4;
        grid-row: 4 / 5;
      `}
    `,
  }

  return (
    <section
      css={styles.section}
      {...props}
    >
      <GatsbyImageFocused
        css={styles.background}
        image={data?.backgroundImage?.gatsbyImageData}
        data={data?.backgroundImage}
        aspectRatio={7 / 4}
      />
      <div css={styles.text}>
        <MarkdownHeading
          css={styles.heading}
          wrapper={'h1'}
        >
          {data?.heading}
        </MarkdownHeading>
        <div css={styles.caveat}>
          <DatoStructuredText data={data?.caveat} />
        </div>
      </div>
      <div css={styles.imageContainer}>
        {data?.images?.map((image, i) => {
          return (
            <GatsbyImageFocused
              css={() => styles.image(i)}
              image={getImage(i)?.image}
              aspectRatio={getImage(i)?.ar}
              data={image}
              key={i}
            />
          )
        })}
      </div>
    </section>
  )
}

export const LandingHeroFragment = graphql`
  fragment LandingHero on DatoCmsLandingHero {
    __typename
    id: originalId
    heading
    caveat {
      value
    }
    images {
      image1: gatsbyImageData(
        imgixParams: {
          fit: "crop"
          crop: "focalpoint"
          ar: "1:1"
          q: 65
        }
        width: 300
        sizes: "(max-width: 540px) 36vw, 18vw"
      )
      image2: gatsbyImageData(
        imgixParams: {
          fit: "crop"
          crop: "focalpoint"
          ar: "4:5"
          q: 65
        }
        width: 600
        sizes: "(max-width: 540px) 66vw, 33vw"
      )
      image3: gatsbyImageData(
        imgixParams: {
          fit: "crop"
          crop: "focalpoint"
          ar: "2:3"
          q: 65
        }
        width: 360
        sizes: "(max-width: 540px) 40vw, 20vw"
      )
      image4: gatsbyImageData(
        imgixParams: {
          fit: "crop"
          crop: "focalpoint"
          ar: "9:5"
          q: 65
        }
        width: 420
        sizes: "(max-width: 540px) 50vw, 25vw"
      )
      ...ImageFocalData
    }
    backgroundImage {
      ...ImageFocalData
      gatsbyImageData(
        imgixParams: {
          fit: "crop"
          crop: "focalpoint"
          ar: "7:4"
          q: 65
          sat: -100
          exp: -40
        }
        width: 1920
        sizes: "100vw"
      )
    }
  }
`
